/**
 * DeleteAccountModal — 2-step destructive confirmation.
 * Step 1 — warning + "Continue" CTA.
 * Step 2 — requires the word "DELETE" typed + password (for email users).
 */
import { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function DeleteAccountModal({ hasPassword = true, onClose, onDeleted }) {
  const [step, setStep] = useState(1);
  const [pw, setPw] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit =
    !busy && confirmText === "DELETE" && (!hasPassword || pw.length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/delete-account", {
        password: hasPassword ? pw : null,
        confirm_text: "DELETE",
      });
      toast.success("Your account is scheduled for deletion in 30 days");
      onDeleted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not delete account");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40"
      data-testid="delete-account-modal"
      onClick={onClose}
    >      <div
        className="bg-white w-full sm:max-w-md sm:rounded-[24px] rounded-t-[24px] p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-red-500" />
            <h3 className="font-display text-[22px]">Delete Account</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="del-close"
            aria-label="Close"
            className="w-9 h-9 -mr-2 grid place-items-center rounded-full hover:bg-canvas2"
          >
            <X size={18} />
          </button>
        </div>

        {step === 1 && (
          <>
            <ul className="text-[13px] text-ink-soft space-y-2 mt-2 mb-4">
              <li>• Your profile, workout history, food logs, body scans, and messages will be permanently removed.</li>
              <li>• You will have 30 days to recover the account by signing back in and contacting support.</li>
              <li>• Active subscriptions will not be auto-refunded — cancel first from Subscription if needed.</li>
              <li>• Some legally-required records (invoices, anti-fraud logs) will be retained as explained in the Privacy Policy.</li>
            </ul>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                data-testid="del-cancel"
                className="flex-1 h-12 rounded-pill border border-line2 font-medium"
                style={{ borderRadius: 100 }}
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                data-testid="del-continue"
                className="flex-1 h-12 rounded-pill font-bold"
                style={{ background: "#E5484D", color: "#fff", borderRadius: 100 }}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="text-[13px] text-ink-soft mt-1 mb-3">
              This action is destructive. To confirm, type <strong>DELETE</strong> below
              {hasPassword ? " and enter your password." : "."}
            </p>
            {hasPassword && (
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-1.5">
                  Your password
                </div>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="del-password"
                  className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none text-[14px]"
                  autoComplete="current-password"
                />
              </div>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-medium mb-1.5">
                Type DELETE to confirm
              </div>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                data-testid="del-confirm"
                className="w-full h-12 px-3 rounded-[12px] border border-line bg-canvas2 outline-none text-[14px] font-mono tracking-wider"
                placeholder="DELETE"
              />
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setStep(1)}
                data-testid="del-back"
                className="flex-1 h-12 rounded-pill border border-line2 font-medium"
                style={{ borderRadius: 100 }}
              >
                Back
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                data-testid="del-confirm-btn"
                className="flex-1 h-12 rounded-pill font-bold disabled:opacity-50"
                style={{ background: "#E5484D", color: "#fff", borderRadius: 100 }}
              >
                {busy ? "Deleting…" : "Delete my account"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
